import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TourAvailabilityParams, ToursApi } from '@model/iceberg/service/tours';
import { TourAvailabilityList, TourAvailabilityResult } from '@model/tours';
import { BaseAction } from '@model/redux';
import { TourActions } from '@state/slices/tours/tourActions';

export enum TourAvailabilityActions {
  FETCH_TOUR_AVAILABILITY = '@TOURS/fetchTourAvailability'
}

export interface TourAvailabilityState {
  data: TourAvailabilityList | null;
  loading: boolean;
  error: string | null;
}

export const INITIAL_TOUR_AVAILABILITY_STATE: TourAvailabilityState = {
  data: null,
  loading: false,
  error: null
};

export interface FetchTourAvailabilityAction extends BaseAction {
  type: TourAvailabilityActions.FETCH_TOUR_AVAILABILITY;
  payload: TourAvailabilityParams;
}

export const fetchTourAvailabilities: (params: TourAvailabilityParams) => FetchTourAvailabilityAction = (
  params: TourAvailabilityParams
) => ({
  type: TourAvailabilityActions.FETCH_TOUR_AVAILABILITY,
  payload: params
});

export const fetchTourAvailability = createAsyncThunk<
  TourAvailabilityList,
  TourAvailabilityParams,
  { rejectValue: string }
>(
  TourAvailabilityActions.FETCH_TOUR_AVAILABILITY, // This is the action type prefix
  async (params: TourAvailabilityParams, { rejectWithValue }) => {
    try {
      const api = new ToursApi();
      const response = await api.getTourAvailability(params);

      if (response?.data) {
        const { availability = {}, cheapest } = response.data;
        const availabilityArray: Array<TourAvailabilityResult> = Object.values(availability);

        return {
          availability: availabilityArray,
          cheapest
        };
      } else {
        return rejectWithValue('No tour availability data received');
      }
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch tour availability');
    }
  }
);

export const tourAvailabilitySlice = createSlice({
  name: TourActions.SLICE_NAME,
  initialState: INITIAL_TOUR_AVAILABILITY_STATE,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTourAvailability.pending, (state) => {
        state.data = null;
        state.loading = true;
      })
      .addCase(fetchTourAvailability.fulfilled, (state, action: PayloadAction<TourAvailabilityList>) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchTourAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An unknown error occurred';
      });
  }
});

export const tourAvailabilityReducer = tourAvailabilitySlice.reducer;
